/* eslint-disable import/order */
import { createError, useRoute, useRouteParams } from '#imports'

import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'

import { HOME } from '~/scopes/home/route-names'
import { ROUTES as PLP_ROUTES } from '~/scopes/product-list/route-names'

import { CMS } from '../routes-names'
import {
  CMS_PAGE_TYPE_BY_ROUTE_NAME,
  PAGE_NAME_BY_PLP_UUID,
} from '../service/content-service.config'
import { dewebviewifyRouteName } from '~/scopes/webviews/utils'
import { PRODUCT_ROUTES } from '~/scopes/product-core/route-names'

function getPageNameByExperiment({
  pageName,
  experiments,
  experimentName,
}: {
  pageName: string
  experiments: ReturnType<typeof useExperiments>
  experimentName: string
}) {
  const variation = experiments[`experiment.${experimentName}`]

  if (!variation || variation === 'noVariation') return pageName

  return variation
}

function getPageNameByRouteName({
  routeName,
  routeParams,
  experiments,
}: {
  routeName: string
  routeParams: ReturnType<typeof useRouteParams>
  experiments: ReturnType<typeof useExperiments>
}) {
  const { pageName } = routeParams

  switch (routeName) {
    case CMS.ABOUT_US:
      return 'about-us'
    case CMS.REFER_FRIEND:
      return 'refer-friend'
    case CMS.REPAIR_AND_CARE:
      return 'repair-and-care'
    case CMS.STATEMENT:
      return 'accessibility-statement'
    case CMS.MILITARY_DISCOUNT:
      return 'military-discount'
    case CMS.STUDENT_DISCOUNT:
      return 'student-discount'
    case CMS.END_FAST_TECH:
      return 'end-fast-tech'
    case CMS.ARTICLE_ROOT_HUB:
      return 'content-hub'
    case CMS.PROMISE:
      return 'back-market-promise'
    case CMS.GIFTS:
      return 'gifts'
    case CMS.GIFT_FINDER_HOME:
      return 'home'
    case CMS.EXPRESS_REPLACEMENT:
      return 'express-replacement'

    case HOME: {
      return getPageNameByExperiment({
        pageName: 'home',
        experiments,
        experimentName: 'homeCMSPageName',
      })
    }
    case CMS.EVENT: {
      if (pageName === 'recycling') {
        return getPageNameByExperiment({
          pageName,
          experiments,
          experimentName: 'recyclingEventPageName',
        })
      }
      if (pageName === 'good-deals') {
        return getPageNameByExperiment({
          pageName,
          experiments,
          experimentName: 'goodDealsEventPageName',
        })
      }
      if (pageName === 'holidays') {
        return getPageNameByExperiment({
          pageName,
          experiments,
          experimentName: 'holidaysEventPageName',
        })
      }

      return pageName
    }

    case CMS.BUYBACK: {
      if (pageName === 'home') {
        return getPageNameByExperiment({
          pageName,
          experiments,
          experimentName: 'homeBuybackPageName',
        })
      }

      return pageName
    }

    case PLP_ROUTES.UNIVERSE: {
      const { market } = useMarketplace()
      const uuid = Array.isArray(routeParams.uuid)
        ? routeParams.uuid.join('')
        : routeParams.uuid
      const pageNames = PAGE_NAME_BY_PLP_UUID[market.countryCode]

      if (!pageNames) {
        throw createError({
          statusCode: 404,
          statusMessage: `Universe page is not available for market ${market.countryCode}`,
          fatal: true,
        })
      }

      const universePage = pageNames[uuid]
      if (!universePage) {
        throw createError({
          statusCode: 404,
          statusMessage: `Unknown universe page UUID: ${uuid}`,
          fatal: true,
        })
      }

      return universePage
    }

    case PRODUCT_ROUTES.MASTER_PRODUCT_PAGE: {
      return routeParams.slug
    }
    default:
      return pageName
  }
}

export function usePageParamsFromRoute() {
  const route = useRoute()
  const routeName = String(route.name)

  const formattedRouteName = dewebviewifyRouteName(routeName)

  const routeParams = useRouteParams()

  const { pageCategory = '', locale } = routeParams
  const experiments = useExperiments()

  const pageType = CMS_PAGE_TYPE_BY_ROUTE_NAME[formattedRouteName]

  const pageName = getPageNameByRouteName({
    experiments,
    routeName: formattedRouteName,
    routeParams,
  })

  return {
    pageType,
    pageName,
    pageCategory,
    locale,
  }
}
